import type { FC } from 'react';
import { useCallback } from 'react';

import { useAudioTrigger } from '../../../../controllers/audioController/hooks';
import { chatCommunicationController } from '../../../../controllers/chatCommunication/ChatCommunicationControllerConnector';
import { ChatTabType, Message } from '../../../../store/slices/chat/types';
import { L12nText } from '../../../text/L12nText';
import { MessageInput } from '../../messageInput/MessageInput';

import styles from './ChatFooter.module.css';

interface ChatFooterProps {
  userId: string;
  dealerName: string;
  selectedTab: ChatTabType;
  tableId: string;
  casinoId: string;
  isGameChatUnavailable: boolean;
  isGameChatBlocked: boolean;
  isLiveSupportChatBlocked: boolean;
  cashierCallback?: () => void;
}

const createMessage = (
  userId: string,
  input: string,
  tableId: string,
  casinoId: string,
  selectedTab: ChatTabType
): Message => ({
  content: input.trim(),
  action: 'COMMAND',
  messageId: `${userId}_${Date.now()}`,
  userId,
  tableId,
  casinoId,
  role: 'GAME_USER',
  tagMessage: selectedTab === ChatTabType.GameChat ? 'GAME_USER' : 'SUPPORT',
  timestamp: Date.now(),
});

export const ChatFooter: FC<ChatFooterProps> = ({
  userId,
  dealerName,
  selectedTab,
  tableId,
  casinoId,
  cashierCallback,
  isGameChatUnavailable,
  isGameChatBlocked,
  isLiveSupportChatBlocked,
}) => {
  const audioTrigger = useAudioTrigger();

  const handleSendMessage = useCallback(
    (input: string): void => {
      if (input.trim() !== '') {
        audioTrigger.play({ name: 'menuClick' });
        // Send message to chat server via websocket
        chatCommunicationController.sendMessage(
          JSON.stringify(createMessage(userId, input, tableId, casinoId, selectedTab))
        );
      }
    },
    [audioTrigger, casinoId, selectedTab, tableId, userId]
  );

  return (
    <div className={styles.chatFooter}>
      <MessageInput
        cashierCallback={cashierCallback}
        onSubmit={handleSendMessage}
        isGameChatUnavailable={isGameChatUnavailable}
        isGameChatBlocked={isGameChatBlocked}
        isLiveSupportChatBlocked={isLiveSupportChatBlocked}
        selectedTab={selectedTab}
      />

      <div className={styles.chatFooter__dealer}>
        <L12nText tk="DEALER" />
        <span>: {dealerName}</span>
      </div>
    </div>
  );
};
